import { useQuery } from '@tanstack/react-query';
import { Clock, Info, KeyRound, ShieldAlert, TriangleAlert } from 'lucide-react';
import { fetchSicherheitslage, fetchVerlauf } from '@/api/sicherheit.api';
import { zahl, zeitpunkt } from '@/components/format';

// ─── Sicherheit und Nutzung ─────────────────────────────────────────────────
//
// Alles hier kommt aus dem Protokoll. Das steht auch auf der Seite: eine
// leere Woche kann heißen „nichts passiert" oder „schon gelöscht", und wer
// das verwechselt, zieht die falschen Schlüsse. Deshalb nennt der Kopf die
// Aufbewahrungsfrist und den ältesten vorhandenen Eintrag.
//
// Balken statt Diagrammbibliothek: es geht um Größenverhältnisse über einen
// Monat, nicht um Ablesegenauigkeit. Ein paar div mit Prozentbreite tun das
// und kosten kein zusätzliches Paket.

function Kennzahl({
  label,
  wert,
  hinweis,
  ton = 'normal',
}: {
  label: string;
  wert: number | string;
  hinweis?: string;
  ton?: 'normal' | 'warnung' | 'gut';
}) {
  const farbe =
    ton === 'warnung' ? 'text-amber-700' : ton === 'gut' ? 'text-emerald-700' : 'text-slate-900';
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <p className="text-xs font-medium uppercase tracking-wider text-slate-400">{label}</p>
      <p className={`mt-1 text-2xl font-semibold ${farbe}`}>{wert}</p>
      {hinweis && <p className="mt-0.5 text-xs text-slate-400">{hinweis}</p>}
    </div>
  );
}

/** Waagerechte Balken für eine Verteilung. Der größte Wert setzt die Skala. */
function Balken({
  werte,
  beschriftung,
  farbe = 'bg-blue-500',
}: {
  werte: number[];
  beschriftung: (i: number) => string;
  farbe?: string;
}) {
  const max = Math.max(1, ...werte);
  return (
    <div className="flex h-32 items-end gap-1">
      {werte.map((w, i) => (
        <div
          key={i}
          className="flex h-full flex-1 flex-col items-center justify-end gap-1"
          title={`${beschriftung(i)}: ${w}`}
        >
          <div
            className={`w-full rounded-t ${farbe}`}
            style={{ height: `${Math.max((w / max) * 100, w > 0 ? 3 : 0)}%` }}
          />
          <span className="text-[10px] text-slate-400">{beschriftung(i)}</span>
        </div>
      ))}
    </div>
  );
}

const WOCHENTAGE = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

export function SicherheitPage() {
  const lage = useQuery({ queryKey: ['admin', 'security'], queryFn: fetchSicherheitslage });
  const verlauf = useQuery({ queryKey: ['admin', 'activity'], queryFn: fetchVerlauf });

  if (lage.isLoading || verlauf.isLoading) {
    return <div className="p-4 text-sm text-slate-500 sm:p-6">Auswertung wird geladen…</div>;
  }

  if (lage.isError || !lage.data || !verlauf.data) {
    return (
      <div className="p-4 sm:p-6">
        <p className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {lage.error instanceof Error ? lage.error.message : 'Auswertung nicht verfügbar.'}
        </p>
      </div>
    );
  }

  const s = lage.data;
  const v = verlauf.data;
  const maxTag = Math.max(1, ...v.jeTag.map((t) => t.anmeldungen + t.sonstige));
  const zweiFaktorAnteil =
    s.konten.gesamt > 0 ? Math.round((s.konten.mitZweiFaktor / s.konten.gesamt) * 100) : 0;

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-4 sm:p-6">
      <header>
        <h1 className="flex items-center gap-2 text-xl font-semibold text-slate-900 sm:text-2xl">
          <ShieldAlert className="h-5 w-5 text-slate-400" aria-hidden="true" />
          Sicherheit &amp; Nutzung
        </h1>
        <p className="mt-0.5 text-sm text-slate-500">
          Aus dem Protokoll gerechnet. Aufbewahrung:{' '}
          {s.aufbewahrungTage === 0 ? 'unbegrenzt' : `${s.aufbewahrungTage} Tage`} · ältester
          Eintrag: {s.aeltesterEintrag ? zeitpunkt(s.aeltesterEintrag) : 'keiner'}
        </p>
      </header>

      {s.aufbewahrungTage > 0 && s.aufbewahrungTage < 30 && (
        <p className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          Die Aufbewahrung steht auf {s.aufbewahrungTage} Tage. Der Verlauf unten reicht
          deshalb nicht über 30 Tage – ältere Einträge sind gelöscht, nicht leer.
        </p>
      )}

      {/* ─── Anmeldungen ──────────────────────────────────────────────── */}
      <section>
        <h2 className="mb-2 text-sm font-semibold text-slate-900">Anmeldungen</h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Kennzahl label="Erfolgreich (24 h)" wert={zahl(s.anmeldungen.letzte24h)} />
          <Kennzahl
            label="Fehlversuche (24 h)"
            wert={zahl(s.fehlversuche.letzte24h)}
            ton={s.fehlversuche.letzte24h > 20 ? 'warnung' : 'normal'}
            hinweis={`${zahl(s.fehlversuche.letzte7d)} in 7 Tagen`}
          />
          <Kennzahl
            label="Abgewiesen (24 h)"
            wert={zahl(s.abgewiesen.letzte24h)}
            hinweis="deaktivierte Konten"
          />
          <Kennzahl
            label="Ausgesperrt (24 h)"
            wert={zahl(s.ausgesperrt.letzte24h)}
            hinweis={
              s.laufendeSperren > 0
                ? `${zahl(s.laufendeSperren)} Zähler laufen`
                : 'kein Zähler läuft'
            }
            ton={s.ausgesperrt.letzte24h > 0 ? 'warnung' : 'normal'}
          />
        </div>
      </section>

      {/* ─── Auffälligkeiten ──────────────────────────────────────────── */}
      <section className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
            <TriangleAlert className="h-4 w-4 text-slate-400" aria-hidden="true" />
            Adressen mit Fehlversuchen (7 Tage)
          </h2>
          {s.auffaelligeAdressen.length === 0 ? (
            <p className="mt-3 text-sm text-slate-400">Keine Fehlversuche im Zeitraum.</p>
          ) : (
            <ul className="mt-3 space-y-1.5">
              {s.auffaelligeAdressen.map((a) => (
                <li key={a.adresse} className="flex items-baseline justify-between gap-3 text-sm">
                  <span className="truncate font-mono text-xs text-slate-700">{a.adresse}</span>
                  <span className="shrink-0 text-slate-500">
                    {zahl(a.versuche)} · {zeitpunkt(a.letzter)}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-3 text-xs text-slate-400">
            Viele Versuche von einer Adresse heißen nicht zwingend Angriff – hinter einem
            gemeinsamen Anschluss sitzen mehrere Leute.
          </p>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <h2 className="text-sm font-semibold text-slate-900">
            Betroffene Konten (7 Tage)
          </h2>
          {s.betroffeneKonten.length === 0 ? (
            <p className="mt-3 text-sm text-slate-400">Keine Fehlversuche im Zeitraum.</p>
          ) : (
            <ul className="mt-3 space-y-1.5">
              {s.betroffeneKonten.map((k) => (
                <li key={k.email} className="flex items-baseline justify-between gap-3 text-sm">
                  <span className="truncate text-slate-700">{k.email}</span>
                  <span className="shrink-0 text-slate-500">
                    {zahl(k.versuche)} · {zeitpunkt(k.letzter)}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-3 text-xs text-slate-400">
            Häufige Fehlversuche auf ein Konto sind ein Grund, dessen Inhaber auf einen
            zweiten Faktor anzusprechen.
          </p>
        </div>
      </section>

      {/* ─── Konten ───────────────────────────────────────────────────── */}
      <section>
        <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-900">
          <KeyRound className="h-4 w-4 text-slate-400" aria-hidden="true" />
          Konten
        </h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Kennzahl label="Konten gesamt" wert={zahl(s.konten.gesamt)} />
          <Kennzahl label="Aktive Sitzungen" wert={zahl(s.aktiveSitzungen)} />
          <Kennzahl
            label="Mit zweitem Faktor"
            wert={`${zweiFaktorAnteil} %`}
            hinweis={`${zahl(s.konten.mitZweiFaktor)} von ${zahl(s.konten.gesamt)}`}
            ton={zweiFaktorAnteil >= 50 ? 'gut' : 'warnung'}
          />
          <Kennzahl label="Deaktiviert" wert={zahl(s.konten.deaktiviert)} />
          <Kennzahl
            label="Offene Betriebszugänge"
            wert={zahl(s.offeneBetriebszugaenge)}
            hinweis="auf fremde Stammbäume"
            ton={s.offeneBetriebszugaenge > 0 ? 'warnung' : 'normal'}
          />
        </div>
      </section>

      {/* ─── Verlauf ──────────────────────────────────────────────────── */}
      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
          <Clock className="h-4 w-4 text-slate-400" aria-hidden="true" />
          Aktivität der letzten 30 Tage
        </h2>
        <p className="mt-0.5 text-xs text-slate-500">
          {zahl(v.grundgesamtheit)} Protokolleinträge · blau: Anmeldungen, grau: übrige Aktionen
        </p>

        <div className="mt-4 flex h-32 items-end gap-[2px]">
          {v.jeTag.map((t) => {
            const gesamt = t.anmeldungen + t.sonstige;
            return (
              <div
                key={t.tag}
                // h-full ist nicht kosmetisch: ohne definierte Höhe der Spalte
                // rechnet der Browser die Prozenthöhen der Balken darunter
                // gegen „auto" und macht sie null – das Diagramm blieb leer,
                // obwohl Zahlen da waren.
                className="flex h-full flex-1 flex-col justify-end"
                title={`${new Date(t.tag).toLocaleDateString('de-DE')}: ${t.anmeldungen} Anmeldungen, ${t.sonstige} übrige`}
              >
                <div
                  className="w-full rounded-t bg-slate-300"
                  style={{ height: `${(t.sonstige / maxTag) * 100}%` }}
                />
                <div
                  className="w-full bg-blue-500"
                  style={{ height: `${(t.anmeldungen / maxTag) * 100}%` }}
                />
                {gesamt === 0 && <div className="h-px w-full bg-slate-200" />}
              </div>
            );
          })}
        </div>
        <div className="mt-1 flex justify-between text-[10px] text-slate-400">
          <span>{new Date(v.jeTag[0]?.tag ?? '').toLocaleDateString('de-DE')}</span>
          <span>heute</span>
        </div>
      </section>

      {/* ─── Nutzungszeiten ───────────────────────────────────────────── */}
      <section className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <h2 className="text-sm font-semibold text-slate-900">Nach Tageszeit</h2>
          <p className="mt-0.5 text-xs text-slate-500">Serverzeit, über die letzten 30 Tage</p>
          <div className="mt-4">
            <Balken
              werte={v.jeStunde}
              beschriftung={(i) => (i % 6 === 0 ? String(i) : '')}
              farbe="bg-blue-400"
            />
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <h2 className="text-sm font-semibold text-slate-900">Nach Wochentag</h2>
          <p className="mt-0.5 text-xs text-slate-500">über die letzten 30 Tage</p>
          <div className="mt-4">
            <Balken werte={v.jeWochentag} beschriftung={(i) => WOCHENTAGE[i]} farbe="bg-blue-400" />
          </div>
        </div>
      </section>

      {/* ─── Listen ───────────────────────────────────────────────────── */}
      <section className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <h2 className="text-sm font-semibold text-slate-900">Häufigste Aktionen</h2>
          <ul className="mt-3 space-y-1.5">
            {v.haeufigsteAktionen.map((a) => (
              <li key={a.action} className="flex items-baseline justify-between gap-3 text-sm">
                <span className="truncate font-mono text-xs text-slate-600">{a.action}</span>
                <span className="shrink-0 text-slate-500">{zahl(a.anzahl)}</span>
              </li>
            ))}
            {v.haeufigsteAktionen.length === 0 && (
              <li className="text-sm text-slate-400">Keine Einträge im Zeitraum.</li>
            )}
          </ul>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <h2 className="text-sm font-semibold text-slate-900">Aktivste Konten</h2>
          <ul className="mt-3 space-y-1.5">
            {v.aktivsteKonten.map((k) => (
              <li key={k.email} className="flex items-baseline justify-between gap-3 text-sm">
                <span className="truncate text-slate-700">{k.email}</span>
                <span className="shrink-0 text-slate-500">{zahl(k.anzahl)}</span>
              </li>
            ))}
            {v.aktivsteKonten.length === 0 && (
              <li className="text-sm text-slate-400">Keine Einträge im Zeitraum.</li>
            )}
          </ul>
        </div>
      </section>
    </div>
  );
}

export default SicherheitPage;
